(() => {
  const selector = "[data-pdp-cart-discount-price]";
  const moneyFormat = () => window.FoxThemeSettings?.money_format;
  const routes = () => window.FoxThemeSettings?.routes || {};
  const staleStorageKey = "hs-active-discount-codes";
  const config = window.HessDiscountPricing || { rules: [] };
  let cartCache = null;
  let pendingCart = null;
  let pendingDiscountTimer = null;
  let scheduleTimer = null;
  const pendingDiscountCodes = new Set();
  const subscribedProductIds = new Set();

  const formatPrice = (amount) => {
    const format = moneyFormat();

    if (typeof window.formatMoney === "function" && format) {
      return window.formatMoney(amount, format);
    }

    // Cookiebot auto-blocking releases the inline FoxThemeSettings script about two
    // seconds after this one runs. Without a format, formatMoney falls back to "$1.00".
    try {
      const currency = window.Shopify?.currency?.active || "EUR";
      return new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(amount / 100);
    } catch {
      return `${(amount / 100).toFixed(2).replace(".", ",")} €`;
    }
  };

  const toList = (value) => String(value || "").split(",").map((item) => item.trim()).filter(Boolean);
  const normalizeCode = (code) => String(code || "").trim().toUpperCase();

  const getCartUrl = () => {
    const cartRoute = routes().cart || "/cart";
    return `${cartRoute.replace(/\/$/, "")}.js`;
  };

  const fetchCart = () => {
    if (pendingCart) return pendingCart;

    pendingCart = fetch(getCartUrl(), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((response) => response.json())
      .then((cart) => {
        cartCache = cart;
        return cart;
      })
      .catch(() => null)
      .finally(() => {
        pendingCart = null;
      });

    return pendingCart;
  };

  const priceSpan = (priceEl, amount) => {
    return `<span>${formatPrice(amount)}</span>`;
  };

  const compareSpan = (priceEl, amount) => {
    return `<span>${formatPrice(amount)}</span>`;
  };

  const parseDiscountCodeFromUrl = (url) => {
    if (!url) return "";

    try {
      const parsedUrl = new URL(url, window.location.origin);
      const discountFromPath = parsedUrl.pathname.match(/\/discount\/([^/?#]+)/i);
      return normalizeCode(discountFromPath ? decodeURIComponent(discountFromPath[1]) : "");
    } catch {
      return "";
    }
  };

  const getUrlDiscountCodes = () => {
    const codes = new Set();
    const params = new URLSearchParams(window.location.search);
    const pathCode = parseDiscountCodeFromUrl(window.location.href);

    if (pathCode) codes.add(pathCode);

    ["discount", "discount_code"].forEach((param) => {
      const code = normalizeCode(params.get(param));
      if (code) codes.add(code);
    });

    return codes;
  };

  const clearStoredDiscountCodes = () => {
    try {
      window.localStorage.removeItem(staleStorageKey);
    } catch {
      // Ignore private browsing or disabled storage.
    }
  };

  const addPendingDiscountCode = (code) => {
    const normalizedCode = normalizeCode(code);
    if (!normalizedCode) return;

    pendingDiscountCodes.add(normalizedCode);
    window.clearTimeout(pendingDiscountTimer);
    pendingDiscountTimer = window.setTimeout(() => {
      pendingDiscountCodes.clear();
      if (cartCache) applyCart(cartCache);
      refresh();
    }, 20000);
  };

  const getDiscountTitle = (discount) => normalizeCode(discount?.title || discount?.code || discount?.discount_application?.title);

  const getRuleCartTitles = (rule) => {
    const titles = new Set();

    (rule.cartTitles || []).forEach((title) => titles.add(normalizeCode(title)));
    (rule.codes || []).forEach((title) => titles.add(normalizeCode(title)));

    return titles;
  };

  const getCartLineDiscountTitles = (item) => {
    const titles = new Set();
    const add = (discount) => {
      const title = getDiscountTitle(discount);
      if (title) titles.add(title);
    };

    (item.discounts || []).forEach(add);
    (item.line_level_discount_allocations || []).forEach(add);
    (item.discount_allocations || []).forEach(add);

    return titles;
  };

  const isExpiredConfiguredDiscountTitle = (title) => {
    const normalizedTitle = normalizeCode(title);
    if (!normalizedTitle) return false;

    return (config.rules || []).some((rule) => {
      if (isRuleWithinSchedule(rule)) return false;
      return getRuleCartTitles(rule).has(normalizedTitle);
    });
  };

  const hasOnlyExpiredConfiguredDiscounts = (item) => {
    const titles = [...getCartLineDiscountTitles(item)];
    if (!titles.length) return false;

    return titles.every(isExpiredConfiguredDiscountTitle);
  };

  const getCartDiscountCodes = (cart) => {
    const codes = new Set();
    const add = (discount, options = {}) => {
      if (options.requireApplicable && discount?.applicable === false) return;

      const title = getDiscountTitle(discount);
      if (title) codes.add(title);
    };

    if (!cart) return codes;

    (cart.discount_codes || []).forEach((discount) => add(discount, { requireApplicable: true }));
    (cart.cart_level_discount_applications || []).forEach(add);
    (cart.discount_applications || []).forEach(add);
    (cart.items || []).forEach((item) => {
      (item.discounts || []).forEach(add);
      (item.line_level_discount_allocations || []).forEach(add);
    });

    return codes;
  };

  const getActiveDiscountCodes = (cart) => {
    const codes = new Set();

    getUrlDiscountCodes().forEach((code) => codes.add(code));
    pendingDiscountCodes.forEach((code) => codes.add(code));
    getCartDiscountCodes(cart).forEach((code) => codes.add(code));

    return codes;
  };

  const isRuleWithinSchedule = (rule) => {
    const now = Date.now();

    if (rule.startsAt) {
      const startsAt = Date.parse(rule.startsAt);
      if (!Number.isNaN(startsAt) && now < startsAt) return false;
    }

    if (rule.endsAt) {
      const endsAt = Date.parse(rule.endsAt);
      if (!Number.isNaN(endsAt) && now > endsAt) return false;
    }

    return true;
  };

  const getNextRuleBoundary = () => {
    const now = Date.now();

    return (config.rules || []).reduce((nextBoundary, rule) => {
      const boundaries = [];
      const startsAt = Date.parse(rule.startsAt || "");
      const endsAt = Date.parse(rule.endsAt || "");

      if (!Number.isNaN(startsAt) && startsAt > now) boundaries.push(startsAt);
      if (!Number.isNaN(endsAt) && endsAt >= now) boundaries.push(endsAt + 1);

      return boundaries.reduce((next, boundary) => {
        if (!next || boundary < next) return boundary;
        return next;
      }, nextBoundary);
    }, 0);
  };

  const scheduleNextRuleBoundary = () => {
    window.clearTimeout(scheduleTimer);
    const nextBoundary = getNextRuleBoundary();
    if (!nextBoundary) return;

    scheduleTimer = window.setTimeout(() => {
      applyConfiguredDiscounts();
      scheduleNextRuleBoundary();
    }, Math.min(nextBoundary - Date.now(), 2147483647));
  };

  const hasMatchingCode = (rule, activeCodes) => {
    if (rule.automatic) return true;

    const codes = (rule.codes || []).map(normalizeCode);
    const prefixes = (rule.codePrefixes || []).map(normalizeCode);

    return [...activeCodes].some((activeCode) => {
      if (codes.includes(activeCode)) return true;
      return prefixes.some((prefix) => activeCode.startsWith(prefix));
    });
  };

  const hasIntersection = (left, right) => {
    if (!left.length || !right.length) return false;
    const rightSet = new Set(right.map(String));
    return left.some((item) => rightSet.has(String(item)));
  };

  const isRuleTargetingPrice = (rule, priceEl) => {
    const targets = rule.targets || {};
    const productId = String(priceEl.dataset.productId || "");
    const variantId = String(priceEl.dataset.variantId || "");
    const collectionIds = toList(priceEl.dataset.collectionIds);

    if (targets.all) return true;
    if ((targets.products || []).map(String).includes(productId)) return true;
    if ((targets.variants || []).map(String).includes(variantId)) return true;
    if (hasIntersection(collectionIds, targets.collections || [])) return true;

    return false;
  };

  const discountFromRule = (rule, priceEl) => {
    const price = Number(priceEl.dataset.price || 0);
    const discount = rule.discount || {};
    let finalPrice = price;
    let percent = 0;

    if (!price) return null;

    if (discount.type === "percentage") {
      percent = Number(discount.value || 0);
      finalPrice = Math.round((price * (100 - percent)) / 100);
    } else if (discount.type === "fixed_amount") {
      const amount = Number(discount.value || 0);
      finalPrice = Math.max(0, price - amount);
      percent = Math.round(((price - finalPrice) * 100) / price);
    }

    if (!percent || finalPrice >= price) return null;

    return {
      originalPrice: price,
      finalPrice,
      savings: price - finalPrice,
      percent,
      badgeText: discount.type === "fixed_amount" ? `-${formatPrice(price - finalPrice)}` : "",
      source: rule.id,
    };
  };

  const getOrCreateSaleBadge = (priceEl) => {
    const existingBadge = priceEl.querySelector(".f-price__badge-sale .f-badge");
    if (existingBadge) return existingBadge;

    let badges = priceEl.querySelector(".f-price__badges");
    if (!badges) {
      badges = document.createElement("div");
      badges.className = "f-price__badges";
      badges.dataset.cartDiscountInjectedBadges = "true";
      priceEl.appendChild(badges);
    }

    const badgeWrapper = document.createElement("div");
    badgeWrapper.className = "f-price__badge-sale";
    badgeWrapper.dataset.type = "percentage";
    badgeWrapper.dataset.cartDiscountInjectedBadge = "true";

    const badge = document.createElement("span");
    badge.className = "f-badge f-badge--sale";
    badgeWrapper.appendChild(badge);
    badges.prepend(badgeWrapper);

    return badge;
  };

  const updateBadge = (priceEl, discount) => {
    const badge = getOrCreateSaleBadge(priceEl);
    badge.textContent = discount.badgeText || `-${discount.percent}%`;
  };

  const resetBadge = (priceEl, price, compareAtPrice) => {
    const injectedBadge = priceEl.querySelector('[data-cart-discount-injected-badge="true"]');
    const injectedBadges = priceEl.querySelector('[data-cart-discount-injected-badges="true"]');

    if (injectedBadge) injectedBadge.remove();
    if (injectedBadges && !injectedBadges.children.length) injectedBadges.remove();

    const badge = priceEl.querySelector(".f-price__badge-sale .f-badge");
    if (!badge) return;

    const type = priceEl.dataset.saleBadgeType;
    if (type === "text") {
      badge.textContent = priceEl.dataset.saleBadgeText || "";
      return;
    }

    const template = priceEl.dataset.saleBadgeTemplate || '<span data-sale-value>[amount]</span>';
    let amount = "";

    if (type === "percentage") {
      amount = `${Math.round(((compareAtPrice - price) * 100) / compareAtPrice)}%`;
    } else {
      amount = formatPrice(compareAtPrice - price);
    }

    badge.innerHTML = template.replace("[amount]", amount);
  };

  const renderBase = (priceEl) => {
    if (priceEl.dataset.cartDiscountApplied !== "true") return;

    const price = Number(priceEl.dataset.price || 0);
    const compareAtPrice = Number(priceEl.dataset.compareAtPrice || 0);
    const hasThemeSale = compareAtPrice > price;
    const regularPrice = priceEl.querySelector(".f-price__regular .f-price-item--regular");
    const salePrice = priceEl.querySelector(".f-price__sale .f-price-item--sale");
    const comparePrice = priceEl.querySelector(".f-price__sale .f-price-item--regular");

    priceEl.classList.remove("f-price--cart-discount");
    priceEl.classList.toggle("f-price--on-sale", hasThemeSale);

    if (regularPrice) regularPrice.innerHTML = priceSpan(priceEl, price);
    if (salePrice) salePrice.innerHTML = priceSpan(priceEl, price);
    if (comparePrice) comparePrice.innerHTML = compareSpan(priceEl, hasThemeSale ? compareAtPrice : price);
    resetBadge(priceEl, price, compareAtPrice);

    delete priceEl.dataset.cartDiscountApplied;
  };

  const findCartDiscount = (cart, variantId) => {
    if (!cart || !Array.isArray(cart.items)) return null;

    const lines = cart.items.filter((item) => {
      if (Number(item.variant_id || item.id) !== variantId) return false;
      return !hasOnlyExpiredConfiguredDiscounts(item);
    });
    if (!lines.length) return null;

    const totals = lines.reduce(
      (sum, item) => {
        const quantity = Number(item.quantity || 1);
        const originalLinePrice = Number(item.original_line_price ?? item.original_price * quantity);
        const finalLinePrice = Number(item.final_line_price ?? item.final_price * quantity);

        sum.quantity += quantity;
        sum.original += originalLinePrice;
        sum.final += finalLinePrice;
        return sum;
      },
      { quantity: 0, original: 0, final: 0 }
    );

    const savings = totals.original - totals.final;
    if (!totals.quantity || savings <= 0) return null;

    return {
      originalPrice: Math.round(totals.original / totals.quantity),
      finalPrice: Math.round(totals.final / totals.quantity),
      savings: Math.round(savings / totals.quantity),
      percent: Math.round((savings * 100) / totals.original),
      source: "cart",
    };
  };

  const findConfiguredDiscount = (priceEl, activeCodes) => {
    return (config.rules || []).reduce((bestDiscount, rule) => {
      if (rule.excludeB2B && priceEl.dataset.customerB2b === "true") return bestDiscount;
      if (rule.minimumQuantity && Number(rule.minimumQuantity) > 1) return bestDiscount;
      if (!isRuleWithinSchedule(rule)) return bestDiscount;
      if (!hasMatchingCode(rule, activeCodes)) return bestDiscount;
      if (!isRuleTargetingPrice(rule, priceEl)) return bestDiscount;

      const discount = discountFromRule(rule, priceEl);
      if (!discount) return bestDiscount;
      if (!bestDiscount || discount.finalPrice < bestDiscount.finalPrice) return discount;

      return bestDiscount;
    }, null);
  };

  const renderDiscount = (priceEl, discount) => {
    const regularPrice = priceEl.querySelector(".f-price__regular .f-price-item--regular");
    const salePrice = priceEl.querySelector(".f-price__sale .f-price-item--sale");
    const comparePrice = priceEl.querySelector(".f-price__sale .f-price-item--regular");

    priceEl.classList.add("f-price--on-sale", "f-price--cart-discount");
    priceEl.dataset.cartDiscountApplied = "true";

    if (regularPrice) regularPrice.innerHTML = priceSpan(priceEl, discount.finalPrice);
    if (salePrice) salePrice.innerHTML = priceSpan(priceEl, discount.finalPrice);
    if (comparePrice) comparePrice.innerHTML = compareSpan(priceEl, discount.originalPrice);
    updateBadge(priceEl, discount);
  };

  const applyCart = (cart, priceEls = document.querySelectorAll(selector)) => {
    const activeCodes = getActiveDiscountCodes(cart);

    priceEls.forEach((priceEl) => {
      const variantId = Number(priceEl.dataset.variantId || 0);
      const discount = findCartDiscount(cart, variantId) || findConfiguredDiscount(priceEl, activeCodes);

      if (discount) {
        renderDiscount(priceEl, discount);
      } else {
        renderBase(priceEl);
      }
    });
  };

  const applyConfiguredDiscounts = () => {
    applyCart(cartCache);
  };

  const updateVariantPriceData = (productId, variant, source) => {
    if (!variant) return [];

    const matchingPrices = [...document.querySelectorAll(selector)].filter((priceEl) => {
      if (priceEl.dataset.productId !== String(productId)) return false;
      if (priceEl.dataset.useVariant !== "true") return false;
      if (!source?.section) return true;

      const stickyAtc = priceEl.closest("sticky-atc-bar");
      return source.section.contains(priceEl) || stickyAtc?.dataset.productId === String(productId);
    });

    matchingPrices.forEach((priceEl) => {
      priceEl.dataset.variantId = String(variant.id);
      priceEl.dataset.price = String(variant.price);
      priceEl.dataset.compareAtPrice = String(variant.compare_at_price || 0);
    });

    applyCart(cartCache, matchingPrices);
    return matchingPrices;
  };

  const subscribeToVariantChanges = (priceEl) => {
    const productId = String(priceEl.dataset.productId || "");
    if (!productId || subscribedProductIds.has(productId) || !window.FoxThemeEvents?.subscribe) return;

    subscribedProductIds.add(productId);
    window.FoxThemeEvents.subscribe(`${productId}__VARIANT_CHANGE`, (variant, source) => {
      const matchingPrices = updateVariantPriceData(productId, variant, source);
      window.setTimeout(() => applyCart(cartCache, matchingPrices), 0);
      refresh();
    });
  };

  const registerPriceElements = (root = document) => {
    if (root instanceof Element && root.matches(selector)) subscribeToVariantChanges(root);
    root.querySelectorAll?.(selector).forEach(subscribeToVariantChanges);
  };

  const refresh = () => fetchCart().then((cart) => cart && applyCart(cart));

  const refreshSoon = () => {
    window.setTimeout(refresh, 350);
    window.setTimeout(refresh, 1400);
  };

  let mutationTimer = null;
  const scheduleApplyConfiguredDiscounts = () => {
    window.clearTimeout(mutationTimer);
    mutationTimer = window.setTimeout(applyConfiguredDiscounts, 80);
  };

  document.addEventListener("DOMContentLoaded", () => {
    clearStoredDiscountCodes();
    registerPriceElements();
    applyConfiguredDiscounts();
    scheduleNextRuleBoundary();
    refresh();
  });
  window.addEventListener("pageshow", () => {
    clearStoredDiscountCodes();
    registerPriceElements();
    applyConfiguredDiscounts();
    scheduleNextRuleBoundary();
    refresh();
  });
  document.addEventListener("click", (event) => {
    const trigger = event.target.closest('[data-discount-action="true"], [data-discount-code], [data-code]');
    if (!trigger) return;

    addPendingDiscountCode(trigger.dataset.discountCode || trigger.dataset.code || parseDiscountCodeFromUrl(trigger.href));
    refreshSoon();
  });

  if (window.FoxThemeEvents?.subscribe) {
    window.FoxThemeEvents.subscribe("ON_CART_UPDATED", (cart) => {
      cartCache = cart;
      applyCart(cart);
    });
    window.FoxThemeEvents.subscribe("ON_ITEM_ADDED", refreshSoon);
  }

  const observer = new MutationObserver((mutations) => {
    const addedPriceRoots = mutations.flatMap((mutation) => {
      return [...mutation.addedNodes].filter((node) => {
        if (!(node instanceof Element)) return false;
        return node.matches(selector) || Boolean(node.querySelector(selector));
      });
    });

    if (addedPriceRoots.length) {
      addedPriceRoots.forEach(registerPriceElements);
      scheduleApplyConfiguredDiscounts();
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener("change", (event) => {
    const stickyAtc = event.target.closest?.("sticky-atc-bar[data-product-id]");
    if (!stickyAtc || event.target.name !== "id" || !stickyAtc.currentVariant) return;

    updateVariantPriceData(stickyAtc.dataset.productId, stickyAtc.currentVariant, { section: stickyAtc });
    refresh();
  });
})();
